import React, { useState, useMemo, useEffect } from "react";
import { Auth, useAuth } from "../auth/AuthContext";
import { OperationError, issueTextOrDefault } from "../DAO";
import { addInstance, renameInstance } from "./InstanceDAO";

export type InstanceFormData = {
   id?: number;
   name: string;
}
type Props = {
   instance: InstanceFormData;
   onCancel: () => void;
   onSave: () => void;
}
export const InstanceForm = ({ instance, onCancel, onSave }: Props): JSX.Element => {
   const auth: Auth = useAuth();
   if (!auth?.user?.permissions?.instance?.canEdit) {
      return <div>You do not have permission to be here."</div>;
   }
   const [isSaving, setIsSaving] = useState<boolean>(false);
   const [errMsg, setErrMsg] = useState<string>("");

   const initialName = instance?.name || "";
   const [name, setName] = useState<string>(initialName);
   const canSave = useMemo<boolean>(() => {
      return instance != null && !isSaving && !!name && name !== initialName;
   }, [name, isSaving]);
   // Clear error message when name changes
   useEffect(() => setErrMsg(""), [name]);

   function save(): void {
      if (canSave) {
         setErrMsg("");
         setIsSaving(true);
         const whenDone: Promise<void> = instance?.id != null ?
            renameInstance(instance.id, name) : addInstance(name);
         whenDone.then(() => {
            setErrMsg("");
            onSave();
         }).catch((err: OperationError) => {
            setErrMsg(issueTextOrDefault(err.issues[0]));
         }).then(() => {
            setIsSaving(false);
         });
      }
   }

   const htmlId = (suffix?: string): string => {
      const prefix = "instance-form";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (
      <article id={htmlId()}>
         <label htmlFor={htmlId("name")}>Instance Name:</label>
         <input id={htmlId("name")} type="text" value={name} disabled={isSaving}
            onChange={e => setName(e.target.value)}
            onBlur={e => setName(e.target.value.trim())} />
         <br />
         <button id={htmlId("cancel")} type="button" onClick={onCancel}>Cancel</button>
         <button id={htmlId("save")} type="button" onClick={save} disabled={!canSave}>Save</button>
         {errMsg && <div className="inline-error-message">{errMsg}</div>}
      </article>
   );
};